import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DEBOUNCE_DELAY, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from './consts'
import { DebouncedSearchFilters, SearchFilters } from './types'

export const useInventoryUrlParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const searchFilters: SearchFilters = {
    articleNumber: searchParams.get('articleNumber') || '',
    itemName: searchParams.get('itemName') || '',
    source: searchParams.get('source') || '',
    category: searchParams.get('category') || '',
  }

  const currentPage = Number(searchParams.get('page')) || DEFAULT_PAGE
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE
  const showKlayderRecommendedProducts =
    searchParams.get('klayderRecommended') === 'true'
  const showWithoutCategories =
    searchParams.get('withoutCategories') === 'true'
  const showWithoutTranslations =
    searchParams.get('withoutTranslations') === 'true'
  const showDraft = searchParams.get('isDraft') === 'true'

  const updateParams = useCallback(
    (updates: Record<string, string | string[] | number | boolean | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        Object.entries(updates).forEach(([key, value]) => {
          if (
            value === null ||
            value === '' ||
            value === false ||
            (Array.isArray(value) && value.length === 0)
          ) {
            next.delete(key)
          } else if (Array.isArray(value)) {
            next.set(key, value.join(','))
          } else {
            next.set(key, String(value))
          }
        })
        return next
      })
    },
    [setSearchParams]
  )

  const setSearchFilters = useCallback(
    (filters: SearchFilters) => {
      updateParams({
        articleNumber: filters.articleNumber,
        itemName: filters.itemName,
        source: filters.source,
        category: filters.category,
        page: DEFAULT_PAGE,
      })
    },
    [updateParams]
  )

  const setCurrentPage = useCallback(
    (page: number) => {
      updateParams({ page: page === DEFAULT_PAGE ? null : page })
    },
    [updateParams]
  )

  const setPageSize = useCallback(
    (size: number) => {
      updateParams({
        pageSize: size === DEFAULT_PAGE_SIZE ? null : size,
        page: DEFAULT_PAGE,
      })
    },
    [updateParams]
  )

  const setShowKlayderRecommendedProducts = useCallback(
    (show: boolean) => {
      updateParams({ klayderRecommended: show, page: DEFAULT_PAGE })
    },
    [updateParams]
  )

  const setShowWithoutCategories = useCallback(
    (show: boolean) => {
      updateParams({ withoutCategories: show, page: DEFAULT_PAGE })
    },
    [updateParams]
  )

  const setShowWithoutTranslations = useCallback(
    (show: boolean) => {
      updateParams({ withoutTranslations: show, page: DEFAULT_PAGE })
    },
    [updateParams]
  )

  const setShowDraft = useCallback(
    (show: boolean) => {
      updateParams({ isDraft: show, page: DEFAULT_PAGE })
    },
    [updateParams]
  )

  const removeSearchFilter = useCallback(
    (key: keyof SearchFilters) => {
      updateParams({ [key]: null, page: DEFAULT_PAGE })
    },
    [updateParams]
  )

  const clearAllFilters = useCallback(() => {
    updateParams({
      articleNumber: null,
      itemName: null,
      source: null,
      category: null,
      klayderRecommended: false,
      withoutCategories: false,
      withoutTranslations: false,
      isDraft: false,
      page: DEFAULT_PAGE,
    })
  }, [updateParams])

  return {
    searchFilters,
    currentPage,
    pageSize,
    showKlayderRecommendedProducts,
    showWithoutCategories,
    showWithoutTranslations,
    showDraft,
    setSearchFilters,
    setCurrentPage,
    setPageSize,
    setShowKlayderRecommendedProducts,
    setShowWithoutCategories,
    setShowWithoutTranslations,
    setShowDraft,
    removeSearchFilter,
    clearAllFilters,
  }
}

export const useDebouncedSearch = (
  searchFilters: SearchFilters
): DebouncedSearchFilters => {
  const [debouncedFilters, setDebouncedFilters] =
    useState<DebouncedSearchFilters>({
      articleNumber: '',
      itemName: '',
      source: '',
      category: '',
    })

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters({
        articleNumber: searchFilters.articleNumber,
        itemName: searchFilters.itemName,
        source: searchFilters.source,
        category: searchFilters.category,
      })
    }, DEBOUNCE_DELAY)

    return () => clearTimeout(timer)
  }, [
    searchFilters.articleNumber,
    searchFilters.itemName,
    searchFilters.source,
    searchFilters.category,
  ])

  return debouncedFilters
}

